import env from "@/configuration/env";

/**
 * Auto-redirect into the React console (toggle layer 2 — see docs/react-ui-migration.md).
 *
 * When a user's effective UI is "react" (their own preferredUi, else their organization's
 * defaultUi, else legacy — resolved server-side and delivered on the organization payload),
 * the legacy app hands the current route to the React gateway instead of rendering it.
 *
 * Everything here is a pure function of store state so it can be unit-tested without a
 * router; index.js owns the one side effect (window.location.replace).
 */

/**
 * The legacy paths the React route map covers.
 *
 * MIRROR OF `routeMap` **and** `legacyAliases` in src/ui-web/apps/console/src/route-map.ts — every
 * entry here is a non-null `legacyPath` in one of those two arrays, and vice versa. Aliases are the
 * legacy child URLs of pages that became a single React route with a section or tab param: the React
 * side decides which section or tab, this list only records that the path is covered. The React app
 * remains the single source of
 * truth for the mapping itself (we never compute a React path); this list only answers
 * "would the gateway know where to send this?". Keeping it in step matters because an
 * unmapped path falls back to the org home, which would silently strand a user trying to
 * reach a legacy-only page (/admin, /version). Set equality with the React map is enforced in CI by
 * src/ui-web/apps/console/src/legacy-redirect-parity.test.ts — update both sides together.
 *
 * NOTE: most of these React targets are still placeholder pages. That is safe because the
 * rollout lever is the server-side org `defaultUi`, which stays "legacy" until the React
 * pages are real; auto-redirect only affects orgs (or users) that have opted in.
 */
export const MAPPED_LEGACY_ROUTES = [
  "/",
  // The Verensics-administrator organization list. One segment, so it cannot collide with the
  // param pattern below.
  "/organizations",
  // The create-organization page, mapped by the org-directory slice's increment 3 (it left
  // RESERVED_LEGACY_ROUTES in the same change). NOTE: isMappedLegacyPath() is a plain any-match with
  // no literal-segment scoring, so this path was already satisfying "/organizations/:orgId" below and
  // the reservation was the only thing making it unmapped — this entry does not change that function's
  // answer. It is here because the parity test asserts set equality with route-map.ts's non-null
  // legacyPaths, which is the mirror this list exists to keep.
  "/organizations/create",
  "/organizations/:orgId/reports",
  "/organizations/:orgId/reports/create",
  "/organizations/:orgId/reports/:interviewId",
  "/organizations/:orgId/investigations",
  "/organizations/:orgId/investigations/create",
  "/organizations/:orgId/investigations/:investigationId",
  "/organizations/:orgId/users",
  "/organizations/:orgId/users/create",
  // Legacy's user detail route redirects to a "/profile" child and has six more children. The bare
  // pattern is the React map's primary mapping; the seven children below are its aliases — each one
  // resolves to the same React page with the tab (and Access dimension) that absorbed it.
  "/organizations/:orgId/users/:userId",
  "/organizations/:orgId/users/:userId/profile",
  "/organizations/:orgId/users/:userId/roles",
  "/organizations/:orgId/users/:userId/regions",
  "/organizations/:orgId/users/:userId/facilities",
  "/organizations/:orgId/users/:userId/functions",
  "/organizations/:orgId/users/:userId/companies",
  "/organizations/:orgId/users/:userId/interviews",
  "/organizations/:orgId",
  // The eleven children of the organization route, aliased to the one React settings page with a
  // "?section=" the React side chooses (two segments were renamed there: ssoconfig and
  // questionexclusions). Listed in legacy's own child order.
  "/organizations/:orgId/profile",
  "/organizations/:orgId/features",
  "/organizations/:orgId/tests",
  "/organizations/:orgId/regions",
  "/organizations/:orgId/facilities",
  "/organizations/:orgId/functions",
  "/organizations/:orgId/companies",
  "/organizations/:orgId/interviews",
  "/organizations/:orgId/questionexclusions",
  "/organizations/:orgId/ssoconfig",
  "/organizations/:orgId/internationalization",
  // The Verensics-administrator tools shell and its children, aliased to the one React platform-tools
  // page with a "?section=" the React side chooses. NOT "/admin/spss": the SPSS tool was dropped rather
  // than ported (docs/platform-tools-implementation.md), so that page keeps rendering here in legacy
  // and must never redirect to a console page that does not have it.
  "/admin",
  "/admin/recalculate",
  "/admin/user-export",
  "/admin/report-details-export",
  "/admin/failed-emails",
  // The auth entrances (auth-edge-flows slice). /signout and /logout land on the console's own
  // /signout page (the global beforeEach fires before signout.js's beforeEnter, so the React page
  // owns the whole sign-out); /signin and /login land home, which is where SignIn.vue pushes an
  // authenticated user anyway. NOT /signin/forgot or /signin/workos/redirect: the redirect page is
  // this app's mid-code-exchange callback landing and must keep rendering here, and forgot-password
  // is the hosted AuthKit page's job for console users. The guard only runs authenticated, so
  // anonymous visits to /signin never redirect regardless (index.js documents this).
  "/signin",
  "/login",
  "/signout",
  "/logout",
];

/**
 * Legacy paths whose shape satisfies a pattern above but which the gateway deliberately
 * treats as unmapped. Mirrors `reservedLegacyPaths` in route-map.ts.
 */
export const RESERVED_LEGACY_ROUTES = [
  "/organizations/:orgId/reports/bulkresults",
  // "/organizations/create" used to be here, because "create" reads as an :orgId and would have
  // redirected to an organization admin page for an organization called "create". It now has a real
  // React route and is listed above instead.
];

const segmentsOf = path => path.split("/").filter(Boolean);

/** Mirrors matchPattern() in route-map.ts: same arity, ":" params match any one segment. */
const matchesPattern = (pattern, path) => {
  const patternSegments = segmentsOf(pattern);
  const pathSegments = segmentsOf(path);
  if (patternSegments.length !== pathSegments.length) return false;

  return patternSegments.every((patternSegment, index) => patternSegment.startsWith(":") || patternSegment === pathSegments[index]);
};

/** Strips the query/hash and any trailing slash, so "/organizations/1/" matches its pattern. */
const normalizePath = fullPath => {
  const withoutQuery = (fullPath || "").split("?")[0].split("#")[0];
  return withoutQuery.replace(/\/+$/, "") || "/";
};

/** True when the React gateway has a mapping for this legacy path. */
export const isMappedLegacyPath = fullPath => {
  const path = normalizePath(fullPath);
  if (RESERVED_LEGACY_ROUTES.some(pattern => matchesPattern(pattern, path))) return false;
  return MAPPED_LEGACY_ROUTES.some(pattern => matchesPattern(pattern, path));
};

/**
 * Per-organization lookups, shared with the organization and feature stores.
 *
 * Both settings are keyed by organization, and the guard must answer for the organization
 * in the TARGET route rather than Vuex's "current" one (which is just organizations[0]).
 * They live here, beside the guard they exist for, because they are pure functions of a
 * payload — the store modules cannot be imported into a unit test without booting the
 * whole store, and this logic is worth testing directly.
 */

/** The UI for one organization entry, failing closed when it is missing or unset. */
export const effectiveUiOf = organization => (organization && organization.effectiveUi) || "legacy";

/** The entry for an organization id, or null when the user is not a member. */
export const findOrganization = (organizations, organizationId) => (organizations || []).find(r => r.id === organizationId) || null;

/** Whether a named organization has a feature. Fails closed on a missing payload or id. */
export const organizationHasFeature = (features, organizationId, feature) => {
  if (!features) return false;
  if (!organizationId) return false;

  const organizationFeatures = features[organizationId];
  return !!organizationFeatures && organizationFeatures.some(r => r === feature);
};

/**
 * The gateway URL for a legacy path. The React side owns the mapping, so we only report
 * where the user is. Deployed, both apps share an origin and the base is empty; local dev
 * serves the console from another port via env.console_base_address.
 */
export const consoleFromLegacyHref = fullPath => {
  const base = env.console_base_address || "";
  return `${base}/console/from-legacy?path=${encodeURIComponent(fullPath)}`;
};

/**
 * The URL to send the user to, or null to stay in the legacy app.
 *
 * Fails closed on every axis: no redirect for a signed-URL session (see below), unless the
 * org feature flag is on (layer 1 stays the kill switch — turning it off stops
 * auto-redirect even while defaultUi is "react"), the effective UI is exactly "react" (a
 * user who explicitly chose legacy beats an org default of react, because the server
 * already resolved that precedence), and the target route is one the gateway can map.
 *
 * `effectiveUi` and `hasReactConsole` must be resolved for the organization in the TARGET
 * route, not the current one — both settings are per organization, and a multi-org user
 * can be visiting an organization that is not their first.
 *
 * `isSignedUrlSession` is not a preference question at all. The root isAuthenticated
 * getter is true whenever X-APP-SIGNED-* params are in the URL, so link-based sessions
 * (notably pages rendered headlessly for PDF export) reach this guard. Those credentials
 * live in the query string and are replayed as headers by the legacy axios layer; the
 * console cannot forward them, so redirecting one produces a sign-in page where the
 * export expected content. A signed link is for a single resource, never a UI choice.
 */
export const resolveReactRedirect = ({ effectiveUi, hasReactConsole, fullPath, isSignedUrlSession }) => {
  if (isSignedUrlSession) return null;
  if (!hasReactConsole) return null;
  if (effectiveUi !== "react") return null;
  if (!fullPath) return null;
  if (!isMappedLegacyPath(fullPath)) return null;

  return consoleFromLegacyHref(fullPath);
};

export default resolveReactRedirect;
